import { lazy, Suspense, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Move } from "lucide-react";
import { AddToBag } from "@/components/AddToBag";
import { Eyebrow, MaskText, Reveal } from "@/components/motion";
import { HowToStory } from "@/components/HowToStory";
import { BLENDER, collectionUrl, discountPct, formatINR, productUrl } from "@/data/catalog";
import { useLiveProduct } from "@/lib/live";
import { EASE } from "@/lib/scroll";

const PhotoRing = lazy(() => import("@/components/three/PhotoRing"));

const CARD = [2.2, 3];
const RADIUS = CARD[0] / (2 * Math.tan(Math.PI / 3));

const FaceButton = ({ face, i, active, onClick }) => (
  <button
    data-testid={`blender-face-${face.id}`}
    aria-pressed={active}
    onClick={onClick}
    className={`group grid w-full grid-cols-[auto_1fr] items-start gap-4 border-b border-white/10 py-5 text-left transition-colors duration-500 ${active ? "text-cream" : "text-stone hover:text-cream"}`}
  >
    <span className={`font-mono text-xs transition-colors ${active ? "text-champagne" : "text-stone"}`}>0{i + 1}</span>
    <span>
      <span className="flex items-center gap-3 font-display text-2xl leading-tight sm:text-3xl">
        {face.title}
        <span className={`h-px flex-1 origin-left bg-champagne transition-transform duration-700 ${active ? "scale-x-100" : "scale-x-0"}`} />
      </span>
      <span className={`mt-1 block text-sm leading-relaxed transition-opacity duration-500 ${active ? "opacity-100" : "opacity-60"}`}>{face.note}</span>
    </span>
  </button>
);

export const BlenderShowcase = () => {
  const [active, setActive] = useState(0);
  const control = useRef(null);
  const face = BLENDER.faces[active];
  const blender = useLiveProduct(BLENDER);
  const off = discountPct(blender.price, blender.compare);

  return (
    <section id="blender" data-testid="blender-section" className="relative overflow-hidden bg-ink py-24 text-cream lg:py-36">
      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
        <div className="relative min-w-0 lg:col-span-6" data-testid="blender-stage">
          <div aria-hidden className="pointer-events-none absolute inset-[12%] rounded-full bg-[radial-gradient(circle,rgba(186,122,45,0.26),transparent_65%)] blur-2xl" />
          <Suspense fallback={<div className="h-[62svh] min-h-[420px] lg:h-[680px]" />}>
            <PhotoRing
              items={BLENDER.faces}
              radius={RADIUS}
              size={CARD}
              body
              sparkles={40}
              dim={0.5}
              pop={0}
              dragK={0.009}
              camera={{ position: [0, 2.4, 8.4], fov: 28 }}
              onActive={setActive}
              controlRef={control}
              className="h-[62svh] min-h-[420px] lg:h-[680px]"
              testId="blender-ring"
            />
          </Suspense>

          <span data-testid="blender-face-index" className="pointer-events-none absolute left-1 top-1 font-mono text-xs uppercase tracking-[0.25em] text-cream/80 sm:left-2 sm:top-2">
            0{active + 1} / 0{BLENDER.faces.length}
          </span>
          <div className="pointer-events-none absolute bottom-1 left-1 right-1 flex items-end justify-between gap-3 sm:bottom-2 sm:left-2 sm:right-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={face.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="rounded-2xl border border-champagne/25 bg-ink/60 px-4 py-3 backdrop-blur-xl"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne">Side 0{active + 1}</p>
                <p data-testid="blender-active-face" className="mt-0.5 font-display text-xl text-cream sm:text-2xl">{face.title}</p>
              </motion.div>
            </AnimatePresence>
            <div className="pointer-events-auto flex items-center gap-2">
              <span className="mr-1 hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-stone sm:flex">
                <Move className="h-3.5 w-3.5" /> <span className="sm:hidden">Swipe to rotate</span><span className="hidden sm:inline">Drag to rotate</span>
              </span>
              <button data-testid="blender-prev" aria-label="Previous side" onClick={() => control.current?.prev()} className="icon-btn h-11 w-11"><ArrowLeft className="h-4 w-4" /></button>
              <button data-testid="blender-next" aria-label="Next side" onClick={() => control.current?.next()} className="icon-btn h-11 w-11"><ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center lg:col-span-6">
          <Eyebrow>05 — VelvetEdge · New</Eyebrow>
          <h2 className="mt-5 font-display text-5xl font-light leading-[0.95] text-cream sm:text-6xl lg:text-7xl">
            <MaskText lines={["Three sides.", <em key="e" className="italic text-champagne">One flawless finish.</em>]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-stone sm:text-base">
              Not your regular egg sponge. The VelvetEdge is a 3D precision blender with a faceted shape that puts control back in your hand — a soft matte velvet surface that works with liquids and creams, damp or dry.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 border-t border-white/10" role="tablist" aria-label="Blender sides">
              {BLENDER.faces.map((f, i) => (
                <FaceButton key={f.id} face={f} i={i} active={i === active} onClick={() => control.current?.goTo(i)} />
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-champagne">{BLENDER.name}</p>
                <p className="mt-2 text-cream">
                  <span data-testid="blender-price" className="font-display text-4xl">{formatINR(blender.price)}</span>
                  {off > 0 && <span className="ml-3 text-sm text-stone line-through">{formatINR(blender.compare)}</span>}
                  {off > 0 && <span className="ml-3 font-mono text-xs text-champagne">−{off}%</span>}
                  {blender.available === false && <span className="ml-3 font-mono text-xs uppercase tracking-[0.16em] text-stone">Sold out</span>}
                </p>
              </div>
              <ul className="flex flex-wrap gap-2" data-testid="blender-specs">
                {BLENDER.specs.map((s) => (
                  <li key={s} className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-stone">{s}</li>
                ))}
              </ul>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <AddToBag p={blender} testId="blender-add-to-bag" className="btn-gold" />
              <a href={productUrl(BLENDER.handle)} data-testid="blender-view-product" className="btn-ghost">View blender</a>
              <a href={collectionUrl(BLENDER.collection)} data-testid="blender-all-sponges" className="nav-link inline-flex items-center gap-1 self-center text-sm text-cream">
                All blenders & sponges <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto mt-16 grid max-w-[1440px] grid-cols-2 gap-3 px-5 sm:gap-5 sm:px-8 lg:mt-24 lg:grid-cols-12 lg:gap-6 lg:px-12" data-testid="blender-looks">
        <Reveal className="col-span-2 lg:col-span-5">
          <HowToStory steps={BLENDER.steps} video={BLENDER.video} />
        </Reveal>
        <Reveal delay={0.08} className="col-span-2 sm:col-span-1 lg:col-span-4">
          <figure className="group relative aspect-[3/4] overflow-hidden bg-card lg:aspect-auto lg:h-full">
            <img src={BLENDER.looks[1].img} alt={BLENDER.looks[1].caption} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
            <figcaption className="absolute bottom-4 left-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white/90 sm:bottom-5 sm:left-5">{BLENDER.looks[1].caption}</figcaption>
          </figure>
        </Reveal>
        <Reveal delay={0.16} className="col-span-2 sm:col-span-1 lg:col-span-3">
          <div className="flex h-full flex-col justify-between border border-gold/25 p-6 sm:p-8">
            <p className="font-display text-3xl leading-tight text-cream sm:text-4xl">
              Soft touch. <em className="italic text-champagne">Sharp control.</em>
            </p>
            <p className="mt-6 text-sm leading-relaxed text-stone">
              The pointed tip reaches under-eyes and the corners of the nose, the flat sides lay product exactly where you place it, and the rounded base buffs everything into skin. One tool, three jobs.
            </p>
            <a href={productUrl(BLENDER.handle)} data-testid="blender-learn-more" className="nav-link mt-8 inline-flex items-center gap-1 self-start text-sm text-cream">
              How to use it <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
